/**
 * Pure filtering for the two deposit tables, plus the data-quality projection.
 *
 * FILTERS CHANGE ROWS, NEVER FIGURES
 * ----------------------------------
 * Nothing in this module sums anything. The KPI row and both summary strips are
 * the backend's snapshot and stay put while a filter is applied — a "total" that
 * shrinks as you type is not a total, and an operator who narrows to insurance
 * must still see what the whole portfolio is worth.
 *
 * NOTHING IS HIDDEN BY DEFAULT
 * ----------------------------
 * Returned and excluded rows are in the list from the start. They are the answer
 * to "why is this deposit not in the total?", and silently dropping them would
 * turn that question into a support ticket. The status filter is how a reader
 * narrows; it is not how the page decides what is worth showing.
 */

const norm = (v) => String(v ?? "").trim().toLowerCase();

/** True when every word of the query appears in at least one of the fields. */
function matchesQuery(query, fields) {
  const q = norm(query);
  if (!q) return true;
  const haystack = fields.map(norm).filter(Boolean).join(" ");
  return q.split(/\s+/).every((word) => haystack.includes(word));
}

/* ─── Tenant ─────────────────────────────────────────────────────────── */

/** The type buckets a reader can narrow to. `untyped` covers a NULL type and any
 *  string outside the validated set — both are "not classified" to a reader. */
export const TENANT_TYPE_FILTERS = ["all", "cash", "insurance", "bank", "untyped"];

const KNOWN_TENANT_TYPES = new Set(["cash", "insurance", "bank", "none"]);

function tenantTypeBucket(item) {
  const t = norm(item?.deposit_type);
  if (!t || !KNOWN_TENANT_TYPES.has(t)) return "untyped";
  return t;
}

export function filterTenantItems(items, { query = "", type = "all", status = "all" } = {}) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => {
    if (type !== "all") {
      const bucket = tenantTypeBucket(item);
      // "none" is a recorded "no deposit" and belongs with the unclassified
      // rows for a reader narrowing the list, not in its own filter option.
      const wanted = type === "untyped" ? ["untyped", "none"] : [type];
      if (!wanted.includes(bucket)) return false;
    }
    if (status !== "all" && item?.security_state !== status) return false;
    return matchesQuery(query, [
      item?.tenant_name,
      item?.reference_number,
      item?.short_unit_id,
      item?.unit_title,
      item?.room_name,
    ]);
  });
}

/* ─── Landlord ───────────────────────────────────────────────────────── */

export const LANDLORD_TYPE_FILTERS = ["all", "insurance", "bank", "cash"];

export function filterLandlordItems(items, { query = "", type = "all" } = {}) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => {
    if (type !== "all" && norm(item?.deposit_type) !== type) return false;
    return matchesQuery(query, [
      item?.short_unit_id,
      item?.title,
      item?.address,
      item?.city,
      // K4a: the Verwaltung is now a column, so it has to be searchable —
      // a name a reader can see and cannot search for reads as a broken box.
      item?.landlord_name,
      // K4b.1: the issuer, for the same reason. The stored value is lowercase
      // and `norm` lowercases the query, so typing "SwissCaution" finds
      // "swisscaution" without a label lookup here.
      item?.provider,
    ]);
  });
}

/**
 * Whether a landlord row is missing the premium its type implies.
 *
 * This is the ONLY quality signal derived client-side, and it exists because the
 * landlord contract carries no per-row status. It mirrors the backend counter
 * `insurance_units_without_annual_premium` exactly — same condition, same
 * meaning — so the row badge and the sidebar count can never disagree. No other
 * landlord rule is invented here: anything else would be this page asserting
 * something the backend has not established.
 */
export function landlordPremiumMissing(item) {
  return (
    norm(item?.deposit_type) === "insurance" &&
    !(Number(item?.annual_premium_chf) > 0)
  );
}

/* ─── Data quality ───────────────────────────────────────────────────── */

/**
 * Severity is assigned by what the counter MEANS, not by how large it is.
 *
 * `bad`  — a row contradicts itself or holds a value outside the vocabulary.
 *          Somebody has to decide which figure is true.
 * `warn` — a value is missing where the rule needs one, so a security is
 *          understated or parked for review.
 * `info` — completeness only; nothing is wrong with a figure.
 */
export const TENANT_QUALITY_ROWS = [
  { key: "legacy_conflicts_canonical", severity: "bad",
    label: "Widersprüchliche Kautionsbeträge (alt vs. aktuell)" },
  { key: "returned_amount_gt_original", severity: "bad",
    label: "Rückzahlung höher als die hinterlegte Kaution" },
  { key: "unknown_deposit_type", severity: "bad",
    label: "Unbekannte Kautionsart" },
  { key: "positive_amount_with_type_none", severity: "warn",
    label: "Betrag erfasst, Kautionsart auf «Keine»" },
  { key: "positive_amount_without_type", severity: "warn",
    label: "Betrag erfasst, keine Kautionsart hinterlegt" },
  { key: "insurance_without_amount", severity: "warn",
    label: "Versicherte Kaution ohne Betrag" },
  { key: "ended_insurance_not_returned", severity: "warn",
    label: "Beendete Versicherung ohne Freigabe" },
  { key: "returned_without_returned_at", severity: "warn",
    label: "Rückzahlung ohne Datum" },
  { key: "legacy_only_amount", severity: "warn",
    label: "Betrag nur im Altfeld hinterlegt" },
  { key: "insurance_without_provider", severity: "info",
    label: "Versicherte Kaution ohne Anbieter" },
];

/** Landlord keys differ from the tenant ones — `unknown_deposit_type_units`,
 *  not `unknown_deposit_type`. Verified against the response schema. */
export const LANDLORD_QUALITY_ROWS = [
  { key: "unknown_deposit_type_units", severity: "bad",
    label: "Unbekannte Sicherheitsart" },
  { key: "insurance_units_without_annual_premium", severity: "warn",
    label: "Versicherte Sicherheit ohne Jahresprämie" },
  // K4b.2. Placed here because it is the premium row's sibling — same rows, one
  // field along. The CARD still sorts by severity (SEVERITY_ORDER), so an `info`
  // entry renders after every `warn` one; this position is for the next reader of
  // this file, not for the screen. Raising it to `warn` to force adjacency would
  // say a missing name is as serious as a missing cost.
  { key: "insurance_units_without_provider", severity: "info",
    label: "Versicherte Sicherheit ohne Anbieter" },
  { key: "premium_on_non_insurance_type", severity: "warn",
    label: "Jahresprämie ohne Versicherungs-Sicherheit" },
  { key: "positive_amount_with_type_none", severity: "warn",
    label: "Betrag erfasst, Sicherheitsart auf «Keine»" },
  { key: "positive_amount_without_type", severity: "warn",
    label: "Betrag erfasst, keine Sicherheitsart hinterlegt" },
];

/**
 * The rows worth showing: a counter at zero is not a finding, and listing ten
 * zeroes would bury the one that is not.
 */
export function selectQualityFindings(dataQuality, rows) {
  if (!dataQuality) return [];
  return rows
    .map((row) => ({ ...row, count: Number(dataQuality[row.key]) || 0 }))
    .filter((row) => row.count > 0);
}
